import { useMemo, useState } from 'react';
import { Card, Divider, Stack, Typography } from '@mui/material';
import QRCodeIcon from '@mui/icons-material/QrCode2Rounded';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { formatAmount } from 'utils/helpers';
import client from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { fetchUserInfo } from 'redux/slices/user/userSlice';
import { buttonWidth, fieldWidth } from 'pages/Invoice/SendInvoiceForm';
import routes from 'constants/routes';
import BasketsAutocomplete from 'components/Common/autocomplete/BasketsAutocomplete';
import ParishesAutocomplete from 'components/Common/autocomplete/ParishesAutocomplete';
import { transferFunds } from 'redux/slices/transaction/transactionDetails';
import { currencyCode } from 'appConfig';
import { fetchMerchantBalance } from 'redux/slices/wallet/merchantBalanceSlice';

interface SendMoneyFormInputs {
    amount: string;
    parishId?: string;
    basketId?: string;
}

const sendMoneyOptions = [
    {
        label: 'Parish',
        value: 'parish',
    },
    {
        label: 'Basket',
        value: 'basket',
    },
];

export default function SendMoneyForm() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { state } = useLocation();
    const defaultSVA = useSelector((state: RootState) => state.wallet?.moneyContainer?.defaultSVA);
    const merchantBalance = useSelector((state: RootState) => state.wallet?.merchantBalance?.balance);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const { name, companyCode, companyId } = state?.parish || {};
    const [selectedItemName, setSelectedItemName] = useState(name || '');
    const [selectedParishId, setSelectedParishId] = useState('');

    const formSchema = useMemo(
        () =>
            yup.object().shape({
                amount: yup
                    .string()
                    .required('Amount is required.')
                    .test('amount', `Amount must be greater than $0.00 `, (value: any) => !(Number(value) < 0.01))
                    .test(
                        'amount',
                        `Amount must be less than $${merchantBalance} `,
                        (value: any) => !(Number(value) > merchantBalance * 100)
                    ),
                parishId: yup.string().when('sendTo', (sendTo, schema) => {
                    if (sendTo[0] === 'parish') {
                        return schema.required('Please select a parish');
                    }
                    return schema;
                }),
                basketId: yup.string().when('sendTo', (sendTo, schema) => {
                    if (sendTo[0] === 'basket') {
                        return schema.required('Please select a basket');
                    }
                    return schema;
                }),
            }),
        [merchantBalance]
    );

    const {
        handleSubmit,
        watch,
        setValue,
        getValues,
        reset,
        trigger,
        formState: { errors },
        control,
    } = useForm<SendMoneyFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            amount: '',
            parishId: companyCode || '',
            basketId: '',
        },
    });
    const handleFormSubmit = () => {
        setModalOpen(true);
    };

    const handleConfirm = async () => {
        const { parishId, basketId, amount } = getValues();
        const _amount = +amount;
        const normalizedValues = { id: Number(basketId), amount: _amount / 100, svaId: defaultSVA };
        try {
            setLoading(true);
            if (!basketId) {
                const { payload }: any = await dispatch(
                    transferFunds({
                        companyCode: parishId || '',
                        containerId: String(defaultSVA),
                        currencyCode,
                        isM2MTransfer: true,
                        amount: _amount / 100,
                    })
                );
                setLoading(false);
                if (payload?.code === 200) {
                    reset();
                    dispatch(fetchUserInfo());
                    dispatch(fetchMerchantBalance());
                    navigate(routes.home.main);
                }
                return;
            }
            const res: any = await client.post('/event/contribute', normalizedValues);
            setLoading(false);
            setModalOpen(false);
            if (res.code === 200) {
                notify('Funds contributed successfully!', 'success');
                reset();
                dispatch(fetchUserInfo());
                dispatch(fetchMerchantBalance());
                navigate(routes.home.main);
            }
        } catch (e) {
            setLoading(false);
            setModalOpen(false);
        }
    };

    return (
        <Card sx={{ p: { xs: 2, lg: 12 }, mx: { xs: 2, lg: 24 }, my: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                Send Money
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                Send money to other parishes, baskets, organizations, or parishioners. Select the recipient, the funding source, and the
                amount to send.
            </Typography>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Stack alignItems="center" gap={5}>
                    <Controller
                        name="parishId"
                        control={control}
                        render={({ field: { value, onChange, ...rest } }) => (
                            <ParishesAutocomplete
                                {...rest}
                                sx={{ width: fieldWidth }}
                                label="SendTo"
                                value={watch('parishId') || null}
                                matchKey="companyCode"
                                defaultSearchText={name}
                                disabled={name}
                                isOptionEqualToValue={(option) => option?.companyCode === watch('parishId')}
                                onChange={(_, option) => {
                                    const parishCompanyId = option?.companyId;
                                    setValue('parishId', option?.companyCode || '');
                                    setSelectedItemName(option?.name || '');
                                    setSelectedParishId(parishCompanyId || '');
                                    trigger('parishId');
                                }}
                                error={!!errors.parishId}
                                helperText={(errors?.parishId as any)?.message}
                            />
                        )}
                    />
                    {!!watch('parishId') && (
                        <Controller
                            name="basketId"
                            control={control}
                            render={({ field: { value, onChange, ...rest } }) => (
                                <BasketsAutocomplete
                                    {...rest}
                                    companyId={selectedParishId || companyId}
                                    isOptionEqualToValue={(option) => option?.id === watch('basketId')}
                                    onChange={(_, value) => {
                                        setValue('basketId', value?.id);
                                        setSelectedItemName(value?.name);
                                        trigger('basketId');
                                    }}
                                    sx={{ width: fieldWidth }}
                                    label="Select a basket"
                                    placeholder="Type recipient"
                                    // icon={<QRCodeIcon />}
                                    error={!!errors.basketId}
                                    helperText={(errors?.basketId as any)?.message}
                                />
                            )}
                        />
                    )}
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                type="currency"
                                sx={{ width: fieldWidth }}
                                width="100%"
                                onChange={(props) => {
                                    field.onChange(props);
                                    trigger('amount');
                                }}
                                label="Amount"
                                placeholder="$0.00"
                                icon={null}
                                error={!!errors.amount}
                                helperText={(errors?.amount as any)?.message}
                            />
                        )}
                    />
                    <TextInput
                        value={`Parish balance - ${formatAmount(merchantBalance || '')}`}
                        icon={null}
                        disabled
                        sx={{ width: fieldWidth }}
                        placeholder="Parish balance - $0,000.00"
                        label="Withdraw From"
                    />
                    <Button type="submit" sx={{ width: buttonWidth }} title="Send" />
                </Stack>
            </form>

            {/* Confirm Invoice Modal */}
            <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle="Confirm Send Money">
                <>
                    <Typography sx={{ fontSize: 18 }}>
                        Are you sure you want to send
                        <br />
                        <strong style={{ marginBottom: 2, fontSize: 20 }}>{formatAmount(getValues().amount, 'cents')}</strong>
                    </Typography>
                    <Typography style={{ whiteSpace: 'nowrap' }}>
                        <p style={{ color: '#1F2937', marginTop: 3, marginBottom: 3 }}>To</p>
                        <strong style={{ fontSize: 20 }}>{selectedItemName}</strong>
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={() => setModalOpen(false)} width={'45%'} sx={{ fontSize: { xs: 14, sm: 18 } }} title="Cancel" />
                        <Button
                            loading={loading}
                            onClick={handleConfirm}
                            width={'45%'}
                            sx={{ fontSize: { xs: 14, sm: 18 } }}
                            title="Confirm"
                        />
                    </div>
                </>
            </Modal>
        </Card>
    );
}
